import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/education-staff.svg";
import WhatsAppButton from "../components/WhatsappButton";
import RondleidingForm from "../components/RondleidingForm";
import { Link } from "react-router-dom";

const EducationalStaff: React.FC = () => {
  const { t } = useLanguage();
  const [openForm, setOpenForm] = React.useState<null | "rondleiding">(null);

  const coreValues = [
    {
      title: t("educationalStaff.coreValues.sensitiveResponsive.title"),
      description: t(
        "educationalStaff.coreValues.sensitiveResponsive.description"
      ),
      icon: "👁️",
      color: "blue",
      practices: t("educationalStaff.coreValues.sensitiveResponsive.practices"),
    },
    {
      title: t("educationalStaff.coreValues.guidingGroupProcesses.title"),
      description: t(
        "educationalStaff.coreValues.guidingGroupProcesses.description"
      ),
      icon: "🤝",
      color: "green",
      practices: t(
        "educationalStaff.coreValues.guidingGroupProcesses.practices"
      ),
    },
    {
      title: t("educationalStaff.coreValues.providingStructure.title"),
      description: t(
        "educationalStaff.coreValues.providingStructure.description"
      ),
      icon: "📋",
      color: "purple",
      practices: t("educationalStaff.coreValues.providingStructure.practices"),
    },
    {
      title: t("educationalStaff.coreValues.allowingAutonomy.title"),
      description: t(
        "educationalStaff.coreValues.allowingAutonomy.description"
      ),
      icon: "🌟",
      color: "blue",
      practices: t("educationalStaff.coreValues.allowingAutonomy.practices"),
    },
    {
      title: t("educationalStaff.coreValues.individualDevelopment.title"),
      description: t(
        "educationalStaff.coreValues.individualDevelopment.description"
      ),
      icon: "📊",
      color: "green",
      practices: t(
        "educationalStaff.coreValues.individualDevelopment.practices"
      ),
    },
    {
      title: t("educationalStaff.coreValues.everyEmployeeMentor.title"),
      description: t(
        "educationalStaff.coreValues.everyEmployeeMentor.description"
      ),
      icon: "👨‍🏫",
      color: "purple",
      practices: t("educationalStaff.coreValues.everyEmployeeMentor.practices"),
    },
  ];

  const developmentAreas = [
    {
      icon: "🔄",
      title: t(
        "educationalStaff.professionalDevelopment.areas.intervision.title"
      ),
      description: t(
        "educationalStaff.professionalDevelopment.areas.intervision.description"
      ),
    },
    {
      icon: "🎓",
      title: t(
        "educationalStaff.professionalDevelopment.areas.education.title"
      ),
      description: t(
        "educationalStaff.professionalDevelopment.areas.education.description"
      ),
    },
    {
      icon: "💼",
      title: t("educationalStaff.professionalDevelopment.areas.coaching.title"),
      description: t(
        "educationalStaff.professionalDevelopment.areas.coaching.description"
      ),
    },
    {
      icon: "⏰",
      title: t(
        "educationalStaff.professionalDevelopment.areas.documentation.title"
      ),
      description: t(
        "educationalStaff.professionalDevelopment.areas.documentation.description"
      ),
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 hero-section-h">
          {/* Decorative SVGs (scaled with container) */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-0 max-w-[220px] sm:max-w-[240px] md:max-w-[280px] lg:max-w-[350px] w-full opacity-100"
            />
          </div>
          <div className="max-w-6xl mx-auto text-center z-10 relative">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-child font-semibold text-black mb-6 leading-tight">
              {t("educationalStaff.title")}
            </h1>
            <p className="text-xl md:text-2xl text-neutral-800 mb-10 max-w-4xl mx-auto leading-relaxed">
              {t("educationalStaff.subtitle")}
            </p>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 rounded-3xl p-8 shadow-soft">
              <h2 className="text-3xl font-child font-semibold text-neutral-800 mb-6">
                {t("educationalStaff.sectionTitle")}
              </h2>
              <p className="text-lg text-neutral-800 leading-relaxed mb-6">
                {t("educationalStaff.introText1")}
              </p>
              <p className="text-lg text-neutral-800 leading-relaxed">
                {t("educationalStaff.introText2")}
              </p>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-child font-semibold text-neutral-800 mb-12 text-center">
              {t("educationalStaff.coreValues.title")}
            </h2>

            <div className="space-y-8">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300"
                >
                  <div className="flex items-center md:space-x-6">
                    <div className="flex-shrink-0 flex">
                      <div
                        className={`w-20 h-20 bg-${value.color}-100 rounded-2xl hidden md:flex items-center justify-center text-4xl`}
                      >
                        {value.icon}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-child font-semibold text-neutral-800 mb-4">
                        {value.title}
                      </h3>
                      <p className="text-lg text-neutral-800 leading-relaxed mb-6">
                        {value.description}
                      </p>
                      <div className={`bg-${value.color}-50 rounded-2xl p-6`}>
                        <h4
                          className={`text-lg font-child font-semibold text-${value.color}-800 mb-3`}
                        >
                          {t("educationalStaff.inPractice")}
                        </h4>
                        <ul className="space-y-2 text-neutral-800">
                          {value.practices.map(
                            (practice: string, practiceIndex: number) => (
                              <li
                                key={practiceIndex}
                                className="flex items-start text-left"
                              >
                                <span
                                  className={`text-${value.color}-500 mr-2`}
                                >
                                  •
                                </span>
                                {practice}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professional Development */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-neutral-900 mb-12 text-center">
              {t("educationalStaff.professionalDevelopment.title")}
            </h2>
            <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft">
              <p className="text-lg text-neutral-800 leading-relaxed mb-8">
                {t("educationalStaff.professionalDevelopment.intro")}
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {developmentAreas.map((area, index) => (
                  <div
                    key={index}
                    className="bg-green-100 rounded-2xl p-6 shadow-soft"
                  >
                    <div className="text-3xl mb-4 text-center">{area.icon}</div>
                    <h4 className="text-lg font-child font-semibold text-neutral-800 mb-3 text-center">
                      {area.title}
                    </h4>
                    <p className="text-neutral-800 text-center leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Sensitive Responsiveness */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-neutral-900 mb-12 text-center">
              {t("educationalStaff.sensitiveResponsiveness.title")}
            </h2>
            <div className="bg-purple-50 rounded-3xl p-8 shadow-soft">
              <p className="text-lg text-neutral-800 leading-relaxed mb-6">
                {t("educationalStaff.sensitiveResponsiveness.intro")}
              </p>
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="bg-[#FBEACD] rounded-2xl p-6 shadow-soft">
                  <h4 className="text-xl font-child font-semibold text-neutral-800 mb-3 flex items-center justify-center">
                    <span className="text-2xl mr-3">🔗</span>
                    {t(
                      "educationalStaff.sensitiveResponsiveness.secureAttachment.title"
                    )}
                  </h4>
                  <p className="text-neutral-800 leading-relaxed">
                    {t(
                      "educationalStaff.sensitiveResponsiveness.secureAttachment.description"
                    )}
                  </p>
                </div>
                <div className="bg-[#FBEACD] rounded-2xl p-6 shadow-soft">
                  <h4 className="text-xl font-child font-semibold text-neutral-800 mb-3 flex items-center justify-center">
                    <span className="text-2xl mr-3">👀</span>
                    {t(
                      "educationalStaff.sensitiveResponsiveness.subtleCues.title"
                    )}
                  </h4>
                  <p className="text-neutral-800 leading-relaxed">
                    {t(
                      "educationalStaff.sensitiveResponsiveness.subtleCues.description"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team Consultation */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-neutral-900 mb-12 text-center">
              {t("educationalStaff.teamConsultation.title")}
            </h2>
            <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft grid md:grid-cols-2 gap-8">
              <div className="bg-yellow-50 rounded-2xl p-6">
                <h4 className="text-xl font-child font-semibold text-neutral-800 mb-4 flex items-center justify-center">
                  {t(
                    "educationalStaff.teamConsultation.teamCollaboration.title"
                  )}
                </h4>
                <p className="text-neutral-800 leading-relaxed mb-4">
                  {t(
                    "educationalStaff.teamConsultation.teamCollaboration.description"
                  )}
                </p>
                <ul className="space-y-2 text-neutral-800">
                  {t(
                    "educationalStaff.teamConsultation.teamCollaboration.practices"
                  ).map((practice: string, i: number) => (
                    <li key={i} className="flex items-start text-left">
                      <span className="text-orange-500 mr-2">•</span>
                      {practice}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 rounded-2xl p-6">
                <h4 className="text-xl font-child font-semibold text-neutral-800 mb-4 flex items-center justify-center">
                  {t(
                    "educationalStaff.teamConsultation.continuousLearning.title"
                  )}
                </h4>
                <p className="text-neutral-800 leading-relaxed mb-4">
                  {t(
                    "educationalStaff.teamConsultation.continuousLearning.description"
                  )}
                </p>
                <ul className="space-y-2 text-neutral-800">
                  {t(
                    "educationalStaff.teamConsultation.continuousLearning.practices"
                  ).map((practice: string, i: number) => (
                    <li key={i} className="flex items-start text-left">
                      <span className="text-purple-500 mr-2">•</span>
                      {practice}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Staff in Action */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-neutral-900 mb-12 text-center">
              {t("educationalStaff.staffInAction.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-green-50 rounded-3xl p-8 shadow-soft">
                <div className="w-full h-48 bg-green-200 rounded-2xl mb-6 flex items-center justify-center">
                  <span className="text-6xl">👨‍🏫</span>
                </div>
                <h3 className="text-2xl font-child font-semibold text-neutral-800 mb-4">
                  {t(
                    "educationalStaff.staffInAction.professionalMentors.title"
                  )}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t(
                    "educationalStaff.staffInAction.professionalMentors.description"
                  )}
                </p>
              </div>
              <div className="bg-purple-50 rounded-3xl p-8 shadow-soft">
                <div className="w-full h-48 bg-purple-200 rounded-2xl mb-6 flex items-center justify-center">
                  <span className="text-6xl">💝</span>
                </div>
                <h3 className="text-2xl font-child font-semibold text-neutral-800 mb-4">
                  {t("educationalStaff.staffInAction.emotionalAnchors.title")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t(
                    "educationalStaff.staffInAction.emotionalAnchors.description"
                  )}
                </p>
              </div>
              <div className="bg-yellow-50 rounded-3xl p-8 shadow-soft">
                <div className="w-full h-48 bg-yellow-200 rounded-2xl mb-6 flex items-center justify-center">
                  <span className="text-6xl">🚀</span>
                </div>
                <h3 className="text-2xl font-child font-semibold text-neutral-800 mb-4">
                  {t("educationalStaff.staffInAction.growthCoaches.title")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t(
                    "educationalStaff.staffInAction.growthCoaches.description"
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 py-16  font-child">
          <div className="rounded-3xl p-12 max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-neutral-900 mb-6">
              {t("educationalStaff.cta.title")}
            </h2>
            <p className="text-xl text-neutral-600 mb-8 leading-relaxed">
              {t("educationalStaff.cta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-gray-900 font-child font-semibold text-lg rounded-full shadow-warm hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
                onClick={() => setOpenForm("rondleiding")}
              >
                {t("educationalStaff.cta.scheduleVisit")}
              </button>
              <Link
                to={"/receiving-policy"}
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-gray-900 font-child font-semibold text-lg rounded-full border-2 hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105"
                style={{ borderColor: "#492A19" }}
              >
                {t("educationalStaff.cta.learnMore")}
              </Link>
            </div>
          </div>
        </section>
      </div>
      {/* Forms Modals */}
      {openForm === "rondleiding" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-6">
          <div className="bg-[#FBEACD] rounded-2xl p-8 max-w-lg w-full relative">
            <button
              onClick={() => setOpenForm(null)}
              className="absolute top-4 right-4 text-2xl"
            >
              &times;
            </button>
            <RondleidingForm />
          </div>
        </div>
      )}
      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default EducationalStaff;
